import { Component, ViewChild, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ProcessCategoryListComponent } from '../../components/process-category-list/process-category-list.component';
import { ProcessCategoryFormComponent } from '../../components/process-category-form/process-category-form.component';
import { ProcessCategoryService } from '../../services/process-category.service';
import { CatalogService } from '@services/data/catalog.service';
import { PageHeaderComponent } from 'app/core/components/page-header/page-header.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { AppSettings } from '@services/settings/app-settings.service';
import { FormMode } from 'app/core/constants/form-mode';
import { PermissionService } from '@services/core/permission.service';
import Swal from 'sweetalert2';
import { NgbModal, NgbModalRef, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-process-category-management',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ProcessCategoryListComponent,
    ProcessCategoryFormComponent,
    PageHeaderComponent,
    MobileFooterComponent,
    NgbModalModule
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './process-category-management.component.html',
  styleUrl: './process-category-management.component.scss'
})
export class ProcessCategoryManagementComponent {
  @ViewChild(ProcessCategoryListComponent) categoryList!: ProcessCategoryListComponent;
  @ViewChild('categoryModal') categoryModal: any;

  private modalRef?: NgbModalRef;
  selectedCategory: any = null;
  categoryForm: FormGroup;
  mode = signal<FormMode>(FormMode.VIEW);
  formMode = FormMode;
  showDetail = false;

  public appSettings = inject(AppSettings);
  readonly perm = inject(PermissionService);
  private fb = inject(FormBuilder);
  private categoryService = inject(ProcessCategoryService);
  private catalogService = inject(CatalogService);
  private modalService = inject(NgbModal);

  public Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true
  });

  constructor() {
    this.categoryForm = this.fb.group({
      uuid: [null],
      slug: [null, [Validators.required, Validators.pattern(/^[A-Z][A-Z0-9_]*$/)]],
      name: [null, [Validators.required]],
      abbr: [null],
      icon: ['fas fa-folder'],
      isActive: [true]
    });
  }

  get showMobileDetail(): boolean {
    return this.appSettings.isMobile() && this.showDetail;
  }

  onCategorySelected(category: any): void {
    this.openEditor(category, FormMode.EDIT);
  }

  private handleSuccess(msg: string): void {
    this.Toast.fire({ icon: 'success', title: msg });
    this.categoryList?.loadCategories();
    this.catalogService.reloadProcessCategories();
    this.mode.set(FormMode.VIEW);
    this.closeDetail();
  }

  private payloadFromForm(): any {
    const raw = this.categoryForm.getRawValue();
    return {
      slug: String(raw.slug ?? '').trim().toUpperCase(),
      name: String(raw.name ?? '').trim(),
      abbr: raw.abbr ? String(raw.abbr).trim().toUpperCase() : null,
      icon: raw.icon ? String(raw.icon).trim() : 'fas fa-folder',
      isActive: raw.isActive !== false
    };
  }

  saveCategory(): void {
    if (this.categoryForm.invalid) {
      this.categoryForm.markAllAsTouched();
      return;
    }
    const data = this.payloadFromForm();
    if (this.mode() === FormMode.EDIT) {
      this.executeUpdate(data);
    } else {
      this.executeCreate(data);
    }
  }

  private executeUpdate(data: any): void {
    const uuid = this.selectedCategory?.uuid;
    if (!uuid) return;
    this.categoryService.update(uuid, data).subscribe({
      next: () => this.handleSuccess('Categoría actualizada'),
      error: (err) => this.showError(err, 'No se pudo actualizar la categoría')
    });
  }

  private executeCreate(data: any): void {
    this.categoryService.create(data).subscribe({
      next: () => this.handleSuccess('Categoría creada'),
      error: (err) => this.showError(err, 'No se pudo crear la categoría')
    });
  }

  private showError(err: any, fallback: string): void {
    const title = err?.error?.message ?? err?.error?.error ?? fallback;
    this.Toast.fire({ icon: 'error', title });
  }

  async deactivateSelected(): Promise<void> {
    if (!this.selectedCategory?.uuid) return;
    const result = await Swal.fire({
      title: '¿Desactivar categoría?',
      text: `“${this.selectedCategory.name}” dejará de ofrecerse al crear procesos.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Desactivar',
      cancelButtonText: 'Cancelar'
    });
    if (!result.isConfirmed) return;

    this.categoryService.update(this.selectedCategory.uuid, {
      ...this.payloadFromForm(),
      isActive: false
    }).subscribe({
      next: () => this.handleSuccess('Categoría desactivada'),
      error: (err) => this.showError(err, 'No se pudo desactivar')
    });
  }

  open(content: any, size: 'md' | 'lg' | 'xl' = 'lg'): void {
    this.modalRef = this.modalService.open(content, { size });
  }

  close(): void {
    this.modalRef?.close();
  }

  closeDetail(): void {
    this.showDetail = false;
    this.selectedCategory = null;
    this.mode.set(FormMode.VIEW);
    this.close();
  }

  newCategoryModal(): void {
    this.openEditor(null, FormMode.CREATE);
  }

  private openEditor(category: any | null, mode: FormMode): void {
    this.selectedCategory = category;
    this.mode.set(mode);
    if (mode === FormMode.CREATE) {
      this.categoryForm.reset({
        uuid: null,
        slug: null,
        name: null,
        abbr: null,
        icon: 'fas fa-folder',
        isActive: true
      });
      this.categoryForm.get('slug')?.enable();
    } else {
      this.categoryForm.reset();
      this.categoryForm.patchValue({
        uuid: category.uuid,
        slug: category.slug,
        name: category.name,
        abbr: category.abbr,
        icon: category.icon || 'fas fa-folder',
        isActive: category.isActive !== false
      });
      this.categoryForm.get('slug')?.disable();
    }
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    if (this.appSettings.isMobile()) {
      this.showDetail = true;
      return;
    }
    this.open(this.categoryModal);
  }
}
